import axios from 'axios';
import { getApiBaseUrl, isNativePlatform } from '../utils/capacitor';

// Get API URL - uses Capacitor utility for platform-aware URL
const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Let the browser automatically set the correct Content-Type (with boundary) for FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    const token = localStorage.getItem('authToken');
    console.log('Request to:', config.url, 'Token exists:', !!token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ Auth header added');
    } else {
      console.warn('⚠️ No token found in localStorage');
    }
    // Add CSRF token for Django
    const csrfToken = document.cookie.split('; ').find(row => row.startsWith('csrftoken='))?.split('=')[1];
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on 401 for login/register endpoints - these return 401 for invalid credentials
    const isAuthEndpoint = error.config?.url?.includes('/api/auth/login') ||
      error.config?.url?.includes('/api/auth/register');

    const isOnAuthPage = window.location.pathname.startsWith('/auth/') ||
      window.location.pathname === '/login' ||
      window.location.pathname === '/';

    if (error.response?.status === 401 && !isAuthEndpoint && !isOnAuthPage) {
      localStorage.removeItem('authToken');
      console.error('401 Unauthorized - Token removed. URL:', error.config?.url);
      console.log('Redirecting to login from:', window.location.pathname);
      window.location.href = '/auth/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login/', credentials),
  register: (data) => api.post('/api/auth/register/', data),
  logout: () => api.post('/api/auth/logout/'),
  getProfile: () => api.get('/api/auth/profile/'),
  forgotPassword: (email) => api.post('/api/auth/forgot-password/', { email }),
  resetPassword: (data) => api.post('/api/auth/reset-password/', data),
};

// Documents API
export const documentsAPI = {
  list: (params, signal) => api.get('/api/documents/', { params, signal }),
  stats: () => api.get('/api/documents/stats/'),
  get: (id) => api.get(`/api/documents/${id}/`),
  upload: (formData) => api.post('/api/documents/upload/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  bulkUpload: (formData) => api.post('/api/documents/bulk-upload/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/api/documents/${id}/delete/`),
  process: (id, model) => api.post(`/api/documents/${id}/process/`, model ? { model } : {}),
  filterOptions: () => api.get('/api/documents/filter-options/'),
  assignCustomer: (id, payload) => api.patch(`/api/documents/${id}/customer/`, payload),
  verify: (id) => api.post(`/api/documents/${id}/verify/`),
  processMultiple: (documentIds, model = 'openai', sendEmail = false) => api.post('/api/documents/batch-process/', {
    document_ids: documentIds,
    model: model,
    send_email_notification: sendEmail
  }),
  getStatus: (id) => api.get(`/api/documents/${id}/status/`),
  search: (query) => api.get('/api/documents/search/', { params: { q: query } }),
  updateStructuredData: (id, data) => api.patch(`/api/documents/${id}/structured-data/`, data),
  translate: (id, opts) => api.post(`/api/documents/${id}/translate/`, opts || {}),
  translateStatus: (taskId) => api.get(`/api/documents/translate-status/${taskId}/`),
  generateStructuredData: (id, model) => api.post(`/api/documents/${id}/generate-structured-data/`, model ? { model } : {}),
  structuredDataStatus: (taskId) => api.get(`/api/documents/structured-data-status/${taskId}/`),

  // Custom fields (user-drawn bounding-box annotations on Structured Data tab)
  // payload: { name, value, page, bbox: {x,y,w,h}, datatype?, label_bbox? }
  addCustomField: (id, payload) => api.post(`/api/documents/${id}/custom-fields/`, payload),
  // payload: any subset of { value, bbox, datatype }
  updateCustomField: (id, fieldName, payload) => api.patch(`/api/documents/${id}/custom-fields/${encodeURIComponent(fieldName)}/`, payload),
  deleteCustomField: (id, fieldName) => api.delete(`/api/documents/${id}/custom-fields/${encodeURIComponent(fieldName)}/delete/`),

  // Place an AI-extracted field that Phase 2 couldn't locate, by drawing a bbox on the page.
  // payload: { name, page, bbox: {x,y,w,h}, label_bbox? }
  placeField: (id, payload) => api.post(`/api/documents/${id}/place-field/`, payload),

  // On-demand paddle_meta (deferred from main detail — ~33MB per page)
  getPagePaddleMeta: (docId, pageId) => api.get(`/api/documents/${docId}/pages/${pageId}/paddle-meta/`),

  // Move / resize an already-placed field's bbox.
  // payload: { name, page, bbox: {x,y,w,h}, label_bbox?, value? }
  moveField: (id, payload) => api.post(`/api/documents/${id}/move-field/`, payload),
};

// Recycle Bin API
export const recycleBinAPI = {
  list: (signal) => api.get('/api/recycle-bin/', { signal }),
  restore: (id) => api.post(`/api/recycle-bin/${id}/restore/`),
  permanentDelete: (id) => api.delete(`/api/recycle-bin/${id}/delete/`),
  bulkPermanentDelete: (ids) => api.post('/api/recycle-bin/bulk-delete/', { ids }),
  empty: () => api.post('/api/recycle-bin/empty/'),
};

// Pages API
export const pagesAPI = {
  get: (id) => api.get(`/api/pages/${id}/`),
  saveCrop: (id, data) => api.post(`/api/pages/${id}/crop/`, data),
  saveTranscription: (id, data) => api.post(`/api/pages/${id}/transcription/`, data),
  runOCR: (id, model) => api.post(`/api/pages/${id}/ocr/`, { model }),
  retryOCR: (id, model) => api.post(`/api/pages/${id}/ocr/retry/`, { model }),
  translate: (id) => api.post(`/api/pages/${id}/translate/`),
  getAttempts: (id) => api.get(`/api/pages/${id}/attempts/`),
  saveStructuredData: (id, data) => api.post(`/api/pages/${id}/structured-data/`, data),
  addToFineTune: (id) => api.post(`/api/pages/${id}/fine-tune/add/`),
  removeFromFineTune: (id) => api.post(`/api/pages/${id}/fine-tune/remove/`),
  submitFeedback: (attemptId, feedback) => api.post(`/api/attempts/${attemptId}/feedback/`, { feedback }),
};

// Document Types API
export const documentTypesAPI = {
  list: () => api.get('/api/document-types/'),
  get: (id) => api.get(`/api/document-types/${id}/`),
  create: (data) => api.post('/api/document-types/create/', data),
  update: (id, data) => api.put(`/api/document-types/${id}/update/`, data),
  delete: (id) => api.delete(`/api/document-types/${id}/delete/`),
  duplicate: (id) => api.post(`/api/document-types/${id}/duplicate/`),
  toggleStatus: (id) => api.post(`/api/document-types/${id}/toggle-status/`),
  history: (id) => api.get(`/api/document-types/${id}/history/`),
};

// Fine-tuning API
export const fineTuneAPI = {
  getDataset: () => api.get('/api/fine-tune/dataset/'),
  getDatasetJSON: () => api.get('/api/fine-tune/dataset/json/'),
  getAvailablePages: () => api.get('/api/fine-tune/available-pages/'),
  addManualEntry: (formData) => api.post('/api/fine-tune/manual-entry/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  removeManualEntry: (id) => api.delete(`/api/fine-tune/manual-entry/${id}/`),
  requestTraining: (data) => api.post('/api/fine-tune/request/', data),
  getRequests: () => api.get('/api/fine-tune/requests/'),
  getRequestDetail: (id) => api.get(`/api/fine-tune/requests/${id}/`),
  checkStatus: (id) => api.get(`/api/fine-tune/requests/${id}/status/`),
};

// API Tokens API
export const tokensAPI = {
  list: () => api.get('/api/tokens/'),
  create: (data) => api.post('/api/tokens/create/', data),
  delete: (id) => api.delete(`/api/tokens/${id}/delete/`),
  toggle: (id) => api.post(`/api/tokens/${id}/toggle/`),
  listClients: () => api.get('/api/clients/'),
  createClient: (data) => api.post('/api/clients/create/', data),
  deleteClient: (id) => api.delete(`/api/clients/${id}/delete/`),
  toggleClient: (id) => api.post(`/api/clients/${id}/toggle/`),
};

// Users API (Admin)
export const usersAPI = {
  list: () => api.get('/api/users/'),
  get: (id) => api.get(`/api/users/${id}/`),
  statistics: () => api.get('/api/users/statistics/'),
  statisticsDetailed: () => api.get('/api/users/statistics/detailed/'),
  myStats: () => api.get('/api/users/my-stats/'),
  orgStats: () => api.get('/api/org/stats/'),
  toggleStatus: (id) => api.post(`/api/admin/users/${id}/toggle-status/`),
  getLogs: (id) => api.get(`/api/admin/users/${id}/logs/`),
  delete: (id) => api.delete(`/api/users/${id}/delete/`),
  changeRole: (id, roleName) => api.post(`/api/admin/users/${id}/roles/`, { role_name: roleName }),
  
  // Enterprise APIs
  requestEnterpriseUpgrade: (data) => api.post('/api/enterprise/upgrade-request/', data),
  saveDraft: (data) => api.post('/api/enterprise/upgrade-request/', { ...data, action: 'draft' }),
  getEnterpriseUpgradeStatus: () => api.get('/api/enterprise/upgrade-request/'),
  
  // Org Admin APIs
  getOrgUsers: () => api.get('/api/org/users/'),
  getOrgUser: (id) => api.get(`/api/org/users/${id}/`),
  createOrgUser: (data) => api.post('/api/org/users/', data),
  updateOrgUser: (id, data) => api.put(`/api/org/users/${id}/`, data),
  deleteOrgUser: (id) => api.delete(`/api/org/users/${id}/`),
  toggleOrgUserStatus: (id) => api.post(`/api/org/users/${id}/toggle-status/`),
  
  // Custom Fields APIs
  getCustomFields: () => api.get('/api/org/custom-fields/'),
  createCustomField: (data) => api.post('/api/org/custom-fields/', data),
  deleteCustomFieldDef: (id) => api.delete(`/api/org/custom-fields/${id}/`),
  
  // System Admin Registration Approval APIs
  getUpgradeRequests: () => api.get('/api/admin/upgrade-requests/'),
  approveUpgradeRequest: (id) => api.post(`/api/admin/upgrade-requests/${id}/approve/`),
  approveUpgradeRequestWithPlan: (id, plan) => api.post(`/api/admin/upgrade-requests/${id}/approve-with-plan/`, { plan }),
  rejectUpgradeRequest: (id, reason) => api.post(`/api/admin/upgrade-requests/${id}/reject/`, { reason }),
  requestMoreInfo: (id, message) => api.post(`/api/admin/upgrade-requests/${id}/request-more-info/`, { message }),
};

// Settings API
export const settingsAPI = {
  getEmailSettings: () => api.get('/api/settings/email/'),
  updateEmailSettings: (data) => api.post('/api/settings/email/update/', data),
  testEmailSettings: () => api.post('/api/settings/email/test/'),
  getAPIKeys: () => api.get('/api/settings/api-keys/'),
  updateAPIKey: (data) => api.post('/api/settings/api-keys/update/', data),
  createSuperuser: (data) => api.post('/api/admin/create-superuser/', data),
  createUser: (data) => api.post('/api/admin/create-user/', data),
};

// Customers (documents are tagged to customers for smart filtering)
export const customersAPI = {
  list: (q) => api.get('/api/customers/', { params: q ? { q } : {} }),
  create: (name) => api.post('/api/customers/', { name }),
  update: (id, name) => api.patch(`/api/customers/${id}/`, { name }),
  delete: (id) => api.delete(`/api/customers/${id}/`),
};

// User API Keys API (for OCR providers)
export const userAPIKeysAPI = {
  get: () => api.get('/api/user-api-keys/'),
  save: (data) => api.post('/api/user-api-keys/', data),
  delete: (provider) => api.delete(`/api/user-api-keys/${provider}/`),
  test: (provider) => api.post(`/api/user-api-keys/${provider}/test/`),
  models: (provider) => api.get(`/api/user-api-keys/${provider}/models/`),
};

// Tasks API
export const tasksAPI = {
  getStatus: (taskId) => api.get(`/api/tasks/${taskId}/status/`),
};

export const idpAPI = {
  list: () => api.get('/api/idp/documents/'),
  get: (id) => api.get(`/api/idp/documents/${id}/`),
  upload: (file, isTrusted = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_trusted', isTrusted);
    return api.post('/api/idp/documents/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  correct: (id, data) => api.patch(`/api/idp/documents/${id}/correct/`, data),
  trust: (id) => api.post(`/api/idp/documents/${id}/trust/`),
  markTraining: (id) => api.post(`/api/idp/documents/${id}/mark_training/`),
  trainingStats: () => api.get('/api/idp/documents/training_stats/'),
  delete: (id) => api.delete(`/api/idp/documents/${id}/`),
  labeledLines: (id) => api.get(`/api/idp/documents/${id}/labeled_lines/`),
  profiles: () => api.get('/api/idp/profiles/'),
};

// RBAC API
export const rbacAPI = {
  getRoles: () => api.get('/api/rbac/roles'),
  getRole: (id) => api.get(`/api/rbac/roles/${id}`),
  getOperations: () => api.get('/api/rbac/operations'),
  createRole: (data) => api.post('/api/rbac/roles', data),
  updateRole: (id, data) => api.put(`/api/rbac/roles/${id}`, data),
  toggleRole: (id) => api.patch(`/api/rbac/roles/${id}/toggle-active`),
  deleteRole: (id) => api.delete(`/api/rbac/roles/${id}`),
};

export default api;
